import type { IConversation } from 'src/api/landlord-messages-api';
import type { ILead } from 'src/services/api';

/**
 * The phone number will be normalized to a simple numeric string
 * Symbol characters and spaces are stripped
 */
export const normalizePhoneNumber = (phoneNumber: string) => {
    const numberChars = phoneNumber.split('').filter((char) => char >= '0' && char <= '9');
    return numberChars.join('');
};

/**
 * If searching for a phone number, phone number matching will always take place using the *normalized* value
 */
export const getConversationSearchTokens = (
    leadsByUuid: Map<string, ILead>,
    conversations: IConversation[]
) => {
    return conversations.reduce<[IConversation, string[]][]>((acc, conversation) => {
        const lead = leadsByUuid.get(conversation.lead_uuid);
        if (!lead) { return acc; }

        const leadUuid = conversation.lead_uuid;
        const leadEmail = lead.email || '';
        const leadName = lead.name || '';
        const leadPhoneNumber = lead.phone_number ? normalizePhoneNumber(lead.phone_number) : '';
        const leadNotes = lead.notes?.contents || '';

        const tokens = [
            leadUuid,
            leadEmail,
            leadName,
            leadPhoneNumber,
            leadNotes,
        ].filter(Boolean).map((token) => token.toLowerCase());

        acc.push([conversation, tokens]);

        return acc;
    }, []);
};

/**
 * Recognize a few common patterns of phone numbers
 * We can use this to optimize search by prioritizing phone number search over tokenized search
 */
export const isPhoneNumberSearchQuery = (searchQuery: string) => {
    const normPhoneNumber = normalizePhoneNumber(searchQuery);

    // if the normalized search query is 10 or 11 digits, it's likely a phone number
    if (normPhoneNumber.length === 10 || normPhoneNumber.length === 11) {
        return true;
    }

    return false;
};

/**
 * @param searchQuery If empty, return all conversations
 */
export const searchConversations = (
    leadsByUuid: Map<string, ILead>,
    conversations: IConversation[],
    searchQuery: string,
) => {
    searchQuery = searchQuery.toLowerCase().trim();

    if (!searchQuery) {
        return conversations;
    }

    const conversationSearchTokens = getConversationSearchTokens(
        leadsByUuid,
        conversations
    );

    const filteredConversations: IConversation[] = [];
    let searchQueryTokens: string[];

    if (isPhoneNumberSearchQuery(searchQuery)) {
        // if we are searching for a phone number, no point in tokenizing the search query
        // normalize it and search for exact matches
        const normQ = normalizePhoneNumber(searchQuery);
        searchQueryTokens = [normQ];
    } else {
        searchQueryTokens = searchQuery
            .split(' ')
            .map((token) => token.trim())
            .filter(Boolean);
    }

    const numQueryToken = searchQueryTokens.length;

    for (const [conversation, conversationTokens] of conversationSearchTokens) {
        let numFound = 0;

        for (const searchToken of searchQueryTokens) {
            let foundSearchToken = false;

            for (const conversationToken of conversationTokens) {
                if (conversationToken.includes(searchToken)) {
                    foundSearchToken = true;
                    break;
                }
            }

            if (!foundSearchToken) {
                break;
            } else {
                numFound += 1;
            }
        }

        // TODO: replace with scoring system
        if (numFound === numQueryToken) {
            filteredConversations.push(conversation);
        }
    }

    return filteredConversations;
};